import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
} from '@mui/icons-material';
import { comptoirPaiementsAPI } from '../../../services/api';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { ComptoirPaiement, ComptoirPaiementImpact } from '../../../types/comptoir';
import { COLOR, NumberField, dh, fDate, num, primaryBtnSx, r2, today } from '../ui';

const RED = '#D32F2F';
const MODES = ['Espèces', 'Chèque', 'Virement', 'Carte', 'Effet'];

const Head: React.FC<{ icon: React.ReactNode; title: string; sub: string; color: string; id: string; onClose: () => void }> = ({ icon, title, sub, color, id, onClose }) => (
  <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
    <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: alpha(color, 0.12), color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
    <Box sx={{ flex: 1 }}>
      <Typography id={id} variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{sub}</Typography>
    </Box>
    <IconButton onClick={onClose} aria-label="Fermer"><CloseIcon /></IconButton>
  </Box>
);

const Liste: React.FC<{ items: string[] }> = ({ items }) => (
  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
    {items.map((b) => <li key={b}><Typography variant="body2">{b}</Typography></li>)}
  </Box>
);

/* ---------------- Modifier un bon de paiement ---------------- */
export const PaiementEditDialog: React.FC<{
  open: boolean; paiement: ComptoirPaiement | null; impact: ComptoirPaiementImpact | null;
  onClose: () => void; onSaved: (p: ComptoirPaiement) => void;
}> = ({ open, paiement: p, impact, onClose, onSaved }) => {
  const [f, setF] = useState({ date: today(), mode: 'Espèces', reference_paiement: '', note: '' });
  const [parts, setParts] = useState<Record<number, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [serverErr, setServerErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && p) {
      setF({ date: p.date, mode: p.mode, reference_paiement: p.reference_paiement || '', note: p.note || '' });
      setParts(Object.fromEntries(p.allocations.map((a) => [a.service_id, String(num(a.montant))])));
      setSubmitted(false); setServerErr('');
    }
  }, [open, p]);
  if (!p || !impact) return null;

  // a later voucher on the same service was computed after this one: amounts and date stay as they are
  const locked = impact.blocages_montant.length > 0;
  const total = r2(p.allocations.reduce((t, a) => t + num(parts[a.service_id]), 0));
  const errors: Record<string, string> = {};
  p.allocations.forEach((a) => { if (!(num(parts[a.service_id]) > 0)) errors[`p${a.service_id}`] = 'Indiquez un montant.'; });
  if (!f.date) errors.date = 'Indiquez la date.';
  else if (f.date > today()) errors.date = 'La date ne peut pas être dans le futur.';
  const show = (k: string) => (submitted ? errors[k] : undefined);

  const save = async () => {
    setSubmitted(true); setServerErr('');
    if (Object.keys(errors).length) return;
    setSaving(true);
    try {
      onSaved(await comptoirPaiementsAPI.update(p.id, {
        date: f.date, mode: f.mode, reference_paiement: f.reference_paiement.trim() || null, note: f.note.trim() || null,
        allocations: p.allocations.map((a) => ({ service_id: a.service_id, montant: r2(num(parts[a.service_id])) })),
      }));
    } catch (e: any) { setServerErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth aria-labelledby="pay-edit-title" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Head id="pay-edit-title" icon={<EditIcon />} color={COLOR} onClose={onClose}
        title={`Modifier ${p.reference}`} sub={`${p.client_nom || ''} · l'ancien et le nouveau contenu sont gardés dans l'historique`} />
      <DialogContent sx={{ pt: '20px !important' }}>
        {serverErr && <Alert severity="error" sx={{ mb: 2 }}>{serverErr}</Alert>}
        {locked && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Montant et date verrouillés :</Typography>
            <Liste items={impact.blocages_montant} />
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField label="Date" type="date" required fullWidth InputLabelProps={{ shrink: true }} value={f.date} disabled={locked}
              inputProps={{ max: today() }} onChange={(e) => setF({ ...f, date: e.target.value })}
              error={!!show('date')} helperText={show('date') || ' '} />
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Mode de paiement" fullWidth value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })}
              helperText={f.mode === 'Espèces' ? 'Entre dans le journal de caisse' : p.mode === 'Espèces' ? 'Quitte le journal de caisse' : ' '}>
              {MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField label="N° chèque / virement" fullWidth value={f.reference_paiement}
              onChange={(e) => setF({ ...f, reference_paiement: e.target.value })} helperText="Facultatif" />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Note" fullWidth value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} helperText="Facultatif" />
          </Grid>
        </Grid>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 1 }}>Répartition</Typography>
        <Stack spacing={1.5}>
          {p.allocations.map((a) => (
            <Stack key={a.service_id} direction="row" spacing={2} alignItems="flex-start">
              <Box sx={{ flex: 1, minWidth: 0, pt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', color: COLOR }}>{a.service_reference}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">{a.designation}</Typography>
              </Box>
              <NumberField label={a.type} required unit="DH" value={parts[a.service_id] || ''} disabled={locked} sx={{ width: 200 }}
                onChange={(v) => setParts({ ...parts, [a.service_id]: v })}
                error={!!show(`p${a.service_id}`)} helperText={show(`p${a.service_id}`) || ' '} />
            </Stack>
          ))}
        </Stack>
        <Paper variant="outlined" sx={{ p: 1.5, mt: 1, borderRadius: 2, display: 'flex', alignItems: 'center', bgcolor: alpha(COLOR, 0.04) }}>
          <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>Montant du bon</Typography>
          <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{dh(total)}</Typography>
        </Paper>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Un montant ne peut pas dépasser ce qui restait dû. Le type (Avance, Solde…) et le reste après sont recalculés.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>Annuler</Button>
        <Button variant="contained" onClick={save} disabled={saving} sx={{ ...primaryBtnSx, minWidth: 140 }}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}>Enregistrer</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---------------- Supprimer un bon de paiement ---------------- */
export const PaiementDeleteDialog: React.FC<{
  open: boolean; paiement: ComptoirPaiement | null; impact: ComptoirPaiementImpact | null;
  onClose: () => void; onDeleted: (reference: string) => void;
}> = ({ open, paiement: p, impact, onClose, onDeleted }) => {
  const [serverErr, setServerErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setServerErr(''); }, [open]);
  if (!p || !impact) return null;
  const blocked = impact.blocages_suppression.length > 0;
  const remove = async () => {
    setServerErr(''); setSaving(true);
    try { onDeleted((await comptoirPaiementsAPI.remove(p.id)).reference); }
    catch (e: any) { setServerErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth aria-labelledby="pay-del-title" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Head id="pay-del-title" icon={<DeleteIcon />} color={RED} onClose={onClose}
        title={`Supprimer ${p.reference}`} sub={blocked ? 'Suppression impossible pour le moment' : "Le bon disparaît ; son numéro n'est pas réutilisé"} />
      <DialogContent sx={{ pt: '20px !important' }}>
        {serverErr && <Alert severity="error" sx={{ mb: 2 }}>{serverErr}</Alert>}
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', bgcolor: alpha(COLOR, 0.04) }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{p.client_nom}</Typography>
            <Typography variant="caption" color="text.secondary">
              {fDate(p.date)} · {p.mode} · {p.allocations.map((a) => `${a.service_reference} ${a.type.toLowerCase()}`).join(', ')}
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{dh(p.montant)}</Typography>
        </Paper>
        {blocked ? (
          <Alert severity="warning">
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>À supprimer ou corriger d'abord :</Typography>
            <Liste items={impact.blocages_suppression} />
          </Alert>
        ) : (
          <Alert severity="info">
            <Liste items={[...impact.avertissements, "La suppression est notée dans l'historique de chaque service concerné."]} />
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>Fermer</Button>
        {!blocked && (
          <Button variant="contained" color="error" onClick={remove} disabled={saving} sx={{ minWidth: 180 }}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}>Supprimer le bon</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
